import { Suspense, useRef, useEffect } from 'react';
import { Outlet, useMatch, useNavigate, useLocation } from 'react-router-dom';
import c from 'classnames';

import { useScrollDisplay } from 'hooks/useScrollDisplay';
import { useScrollMemo } from '@repo/ui';
import { ScrollHandler } from '@repo/utils';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useAccountsAndKits from 'services/account/useAccountsAndKits';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';

import {
  Sidebar,
  Spinfinity,
  Header,
  PageContainer,
  ContainerSizes,
  BackButton,
} from 'components';
import MobileNav from '../../MobileNav/MobileNav';
import useFeature from 'services/features/useFeature';
import ResultsAnnouncementBanner from 'components/ResultsAnnouncementBanner/ResultsAnnouncementBanner';
import { TinyAI, TinyAIProvider } from '../../TinyAI';

import styles from './ResponsiveLayout.module.scss';

const scrollHandler = ScrollHandler.instance;

export const PageAnalytics = ({ children }: { children: React.ReactNode }) => {
  useAnalytics().pageEvent();
  return <>{children}</>;
};
const ResponsiveLayout = () => {
  useAccountsAndKits();
  const { currentTinyAccount, currentKit } = useCurrentKitAndTinyAccount();
  const navigate = useNavigate();
  const appWrapperRef = useRef<HTMLDivElement>(null);
  useScrollMemo(useLocation, {
    pathnameFunctionKey: 'pathname',
    containerRef: appWrapperRef,
  });
  const { pathname } = useLocation();
  const { isFeatureEnabled: showResultsV2Banner } =
    useFeature('results_v2_banner');

  const isAllMetricsPage = !!useMatch('/results/:kitId/insights/all-insights');
  const isResultsKitRoute = !!useMatch('/results/:kitId/*');
  const isActionPlanPage = useMatch('/action-plan/:kitId/*');
  const isTopLevelResults = useMatch({ path: '/results/:kitId', end: true });
  const isTopLevelActionPlan = useMatch({
    path: '/action-plan/:kitId',
    end: true,
  });
  const allMetricsScrollPosKey = `allMetricsScrollPos_${currentKit?.id}`;

  const isBackButtonVisible = useScrollDisplay({
    resetOnNavigate: true,
    threshold: 50,
    action: 'show',
    lockStateOnThreshold: true,
    scrollableElRef: appWrapperRef,
  });

  const goBack = () => {
    scrollHandler.setScrollRestoration();
    navigate(-1);
  };

  const showBackButton =
    (isResultsKitRoute || isActionPlanPage) &&
    !(isTopLevelResults || isTopLevelActionPlan) &&
    isBackButtonVisible;

  useEffect(() => {
    const scrollContainer = appWrapperRef.current;

    const handleScroll = () => {
      if (scrollContainer) {
        sessionStorage.setItem(
          allMetricsScrollPosKey,
          scrollContainer.scrollTop.toString(),
        );
      }
    };

    if (isAllMetricsPage && scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (isAllMetricsPage && scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isAllMetricsPage, allMetricsScrollPosKey]);

  useEffect(() => {
    if (isAllMetricsPage && appWrapperRef.current) {
      const savedPosition = sessionStorage.getItem(allMetricsScrollPosKey);
      if (savedPosition) {
        const scrollPosition = parseInt(savedPosition, 10);

        // Use double RAF for Safari compatibility - ensures DOM is fully rendered
        const rafId1 = requestAnimationFrame(() => {
          const rafId2 = requestAnimationFrame(() => {
            if (appWrapperRef.current) {
              appWrapperRef.current.scrollTop = scrollPosition;
            }
          });
          // Store rafId2 for cleanup if needed
          return () => cancelAnimationFrame(rafId2);
        });

        return () => cancelAnimationFrame(rafId1);
      }
    }
  }, [isAllMetricsPage, allMetricsScrollPosKey]);

  useEffect(() => {
    if (appWrapperRef.current && !isAllMetricsPage) {
      appWrapperRef.current.scrollTo(0, 0);
    }
  }, [pathname, isAllMetricsPage]);

  return (
    <TinyAIProvider>
      <div className={styles.rootContainer}>
        <div className={styles.sidebar}>
          <Sidebar />
        </div>
        <div className={styles.appWrapper} ref={appWrapperRef} id='appWrapper'>
          <div className={styles.contentContainer} id='content-container'>
            <PageContainer size={ContainerSizes.LG}>
              {/* Render header for results pages */}
              {showBackButton && (
                <div
                  className={c(
                    styles.floatingBackButtonContainer,
                    'hide-on-desktop',
                  )}
                >
                  <BackButton variant='button' goBack={goBack} />
                </div>
              )}

              {isResultsKitRoute && isTopLevelResults && (
                <>
                  {showResultsV2Banner && (
                    <div className={styles.banner}>
                      <ResultsAnnouncementBanner
                        mainText='New look, same results'
                        secondaryText='Read our blog on how the summary page has changed.'
                        linkText='Read now'
                        linkUrl='https://www.tinyhealth.com/blog/unlock-your-microbiome-insights-with-our-new-results-view'
                      />
                    </div>
                  )}
                  <Header
                    type='full'
                    title={`${currentTinyAccount?.first_name}'s results`}
                    containerRef={appWrapperRef}
                  />
                </>
              )}
              <Suspense
                fallback={
                  <div className={styles.spinfinityWrapper}>
                    <Spinfinity />
                  </div>
                }
              >
                <PageAnalytics>
                  <div className={styles.contentWrapper}>
                    <Outlet />
                  </div>
                </PageAnalytics>
              </Suspense>
            </PageContainer>
          </div>
        </div>
        <MobileNav />
        <TinyAI scrollableElRef={appWrapperRef} />
      </div>
    </TinyAIProvider>
  );
};
export default ResponsiveLayout;
